/**
 * This module searches for any `noscript` and `template` tags with the `.lazyload` class and then waits for the page to become idle before instanciating them
 *
 * <br/><br/><strong>Note:</strong> This module does <strong>not</strong> control how images are lazy loaded
 * @module lazyload
 */

/**
 * Finds all the `noscript` and `template` tags in the document to loop over
 *
 * @yields {DOMElement} - the next DOMElement in the collection
 */
function* findLazyElements() {
  const blocks = [
    ...Array.from(
      <NodeListOf<HTMLElement>>(
        document.querySelectorAll('noscript.lazyload')
      )
    ).map((lazy) =>
      parseHTML(lazy.innerText).map((elm) => ({
        elm,
        parent: lazy.parentNode
      }))
    ),
    ...Array.from(
      <NodeListOf<HTMLTemplateElement>>(
        document.querySelectorAll('template.lazyload')
      )
    ).map((lazy) =>
      Array.from((lazy.content.cloneNode(true) as HTMLTemplateElement).children)
        .map((elem) => ({ elem, parent: lazy.parentNode }))
        .map((item) => {
          if (item.elem.tagName !== 'SCRIPT') return item;
          item.elem = Object.assign(item.elem.cloneNode(true) as Element, {
            async: false
          });
          return item;
        })
    )
  ];

  for (const block of blocks) {
    yield* block;
  }
}

/**
 * Parses a HTML string and returns an array of all direct descendant children...
 *
 * @param {HTMLString} html - String of HTML to parse
 * @return {Array<DOMElement>} - Returns an array of all direct descendant children
 *
 * @example
 * const data = parseHTML("<div>1</div><div>2</div>");
 */
function parseHTML(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return Array.from(div.children);
}

/**
 * This method will lazyily load up any matching DOMElements that have `.lazyload`. It will delay the loading of them until a `requestIdleCallback`
 *
 * @returns {Promise} Resovles the promise when all elements have been added to the page
 *
 * @example
 * (require("lazyload"))()
 *  .then(() => console.log("All lazy elements loaded"));
 */
export default () =>
  new Promise<void>((resolve) => {
    const it = findLazyElements();

    window.requestIdleCallback(function f() {
      const { value, done } = it.next();
      if (done) {
        resolve();
        return;
      }
      (value as any).parent.appendChild((value as any).elm);
      window.requestIdleCallback(f);
    });
    return Promise;
  });
